import React, { useEffect, useMemo, useState } from 'react'
import { disconnectGmail, getDealEmails, getEmailAccounts, getEmailReview, resolveEmailReview, startGmailConnect, type EmailAccount, type EmailReviewItem, type LeadEmail } from './crmApi'
import { CloseButton, crmToast, IconMailIn, IconMailOut, IconPaperclip, Overlay, SearchInput } from './crmUi'
import { useAuth } from '../../contexts/AuthContext'
import { apiFetch } from '../../utils/api'

/**
 * Emails tab (Lee, 19–20 Aug 2026) — the lead's mini-inbox, v2:
 *   • STICKY header: mailbox chips / connect button, sender + direction +
 *     attachments filters, search — stays put while the list scrolls.
 *   • Search covers subject, participants AND body — matching rows show the
 *     matched line with the term highlighted; expanded bodies highlight too.
 *   • Sort: newest (default) / oldest / attachments first. Pages of 25.
 *   • attachment count on collapsed rows; attachment chips when expanded.
 */

const PAGE_SIZE = 25

const fmtWhen = (iso: string) => {
  const d = new Date(iso)
  return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })} · ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`
}
const fmtSize = (b: number) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b > 1024 ? `${Math.round(b / 1024)} KB` : `${b} B`)
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** Wrap every match of `q` in <mark>. */
const Highlight: React.FC<{ text: string; q: string }> = ({ text, q }) => {
  if (!q) return <>{text}</>
  const parts = text.split(new RegExp(`(${escapeRe(q)})`, 'ig'))
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === q.toLowerCase()
          ? <mark key={i} className="bg-amber-200 text-txt-primary rounded-sm px-0.5">{p}</mark>
          : <React.Fragment key={i}>{p}</React.Fragment>
      )}
    </>
  )
}

/** First line of the body containing the search term — the "matched line". */
const matchedLine = (m: LeadEmail, q: string): string | null => {
  if (!q || !m.body) return null
  const needle = q.toLowerCase()
  for (const line of m.body.split(/\n+/)) {
    if (line.toLowerCase().includes(needle)) return line.trim().slice(0, 220)
  }
  return null
}

/** Clickable attachment — fetched LIVE from Gmail through the API (bytes are
 *  never stored server-side). PDFs/images open in a tab; the rest download. */
const AttachmentChip: React.FC<{ emailId: number; index: number; filename: string; mimeType: string; sizeBytes: number }> = ({ emailId, index, filename, mimeType, sizeBytes }) => {
  const [busy, setBusy] = useState(false)
  const openIt = async () => {
    if (busy) return
    setBusy(true)
    try {
      const resp = await apiFetch(`/crm/emails/${emailId}/attachments/${index}/download`)
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      if (/^application\/pdf|^image\//.test(mimeType)) {
        window.open(url, '_blank')
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e: any) {
      crmToast(e?.message || 'Could not fetch the attachment', 'error')
    } finally {
      setBusy(false)
    }
  }
  return (
    <button onClick={openIt} disabled={busy}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-surface-secondary text-txt-secondary border border-border hover:border-txt-tertiary hover:text-txt-primary disabled:opacity-60"
      title={`${mimeType || 'file'} — fetched live from the mailbox, click to ${/^application\/pdf|^image\//.test(mimeType) ? 'view' : 'download'}`}>
      {busy ? <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <IconPaperclip className="w-3 h-3" />} {filename} · {fmtSize(sizeBytes)}
    </button>
  )
}

/** "Name <addr>" → { name, addr } for Gmail-style sender lines. */
const parseAddr = (raw: string): { name: string; addr: string } => {
  const m = raw.match(/^\s*"?([^"<]*)"?\s*<([^>]+)>\s*$/)
  if (m && m[1].trim()) return { name: m[1].trim(), addr: m[2].trim() }
  const addr = raw.replace(/[<>]/g, '').trim()
  return { name: addr.split('@')[0], addr }
}
const initialOf = (s0: string) => (s0.trim()[0] || '?').toUpperCase()
const AV_COLORS = ['#0ea5e9', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#6366f1', '#ec4899', '#14b8a6']
const avColor = (s0: string) => AV_COLORS[[...s0].reduce((a, c) => a + c.charCodeAt(0), 0) % AV_COLORS.length]
const fmtShort = (iso: string) => {
  const d = new Date(iso); const now = new Date()
  return d.toDateString() === now.toDateString()
    ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric' })
}

/** One message inside an open thread — Gmail-style: avatar, sender, date;
 *  collapsed = one-line snippet, expanded = full body + attachments. */
const MessageItem: React.FC<{ m: LeadEmail; q: string; open: boolean; onToggle: () => void; last: boolean }> = ({ m, q, open, onToggle, last }) => {
  const sender = m.direction === 'in' ? parseAddr(m.from) : parseAddr(m.account)
  const toLine = m.direction === 'in' ? `to ${m.account}` : `to ${m.to.map((t) => parseAddr(t).name).join(', ') || '—'}`
  return (
    <div className={`${last ? '' : 'border-b border-border/70'}`}>
      <button onClick={onToggle} className="w-full text-left px-4 py-2.5 flex items-start gap-3 hover:bg-surface-hover/40">
        <span className="w-8 h-8 rounded-full text-white text-[13px] font-bold flex items-center justify-center shrink-0 mt-0.5" style={{ background: avColor(sender.addr) }}>
          {initialOf(sender.name)}
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-baseline gap-2 min-w-0">
            <span className="text-[13px] font-bold text-txt-primary truncate">{sender.name}</span>
            {m.direction === 'out' && <span className="text-[9px] font-bold text-txt-tertiary border border-border rounded px-1 shrink-0">SENT</span>}
            {m.attachments.length > 0 && !open && <IconPaperclip className="w-3 h-3 text-txt-tertiary shrink-0" />}
            <span className="ml-auto text-[11px] text-txt-tertiary shrink-0 tabular-nums">{fmtShort(m.sentAt)}</span>
          </span>
          {open
            ? <span className="block text-[11px] text-txt-tertiary truncate">{sender.addr} · {toLine}{m.cc.length > 0 ? ` · cc ${m.cc.map((c) => parseAddr(c).name).join(', ')}` : ''}</span>
            : <span className="block text-[12px] text-txt-secondary truncate">{m.snippet || (m.body || '').split(/\n+/)[0] || '(empty)'}</span>}
        </span>
      </button>
      {open && (
        <div className="pl-[60px] pr-5 pb-4">
          <div className="text-[13.5px] text-txt-primary whitespace-pre-wrap leading-[1.65] max-w-[640px] [overflow-wrap:anywhere] max-h-[480px] overflow-y-auto">
            {q ? <Highlight text={m.body || m.snippet || '(empty)'} q={q} /> : (m.body || m.snippet || '(empty)')}
          </div>
          {m.attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-border/70">
              {m.attachments.map((a, i) => (
                <AttachmentChip key={i} emailId={m.id} index={i} filename={a.filename} mimeType={a.mimeType} sizeBytes={a.sizeBytes} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** ONE THREAD = ONE CARD (Lee, 14 Oct 2026). Collapsed: subject + people +
 *  count + latest snippet, like a Gmail inbox row. Expanded: every message
 *  in full, oldest → newest (Lee: no folded one-liners). */
const ThreadCard: React.FC<{ msgs: LeadEmail[]; q: string }> = ({ msgs, q }) => {
  const [open, setOpen] = useState(false)
  const [openMsgs, setOpenMsgs] = useState<Set<number>>(new Set())
  const ordered = useMemo(() => [...msgs].sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime()), [msgs])
  const latest = ordered[ordered.length - 1]
  const attCount = ordered.reduce((a, m) => a + m.attachments.length, 0)
  const people = useMemo(() => {
    const names: string[] = []
    for (const m of ordered) {
      const n = (m.direction === 'in' ? parseAddr(m.from) : parseAddr(m.account)).name
      if (!names.includes(n)) names.push(n)
    }
    return names
  }, [ordered])
  const hit = q ? ordered.map((m) => matchedLine(m, q)).find(Boolean) : null
  const toggle = () => {
    // ALL messages open in full when the thread expands (Lee, 14 Oct 2026) —
    // no folded one-liners; a message header click can still collapse one.
    if (!open) setOpenMsgs(new Set(ordered.map((m) => m.id)))
    setOpen((o) => !o)
  }
  const toggleMsg = (id: number) => setOpenMsgs((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })
  return (
    <div className="rounded-xl border border-border bg-surface overflow-hidden hover:shadow-sm transition-shadow">
      <button onClick={toggle} className={`w-full text-left px-4 py-3 ${open ? 'bg-surface-secondary/50 border-b border-border' : 'hover:bg-surface-hover/40'}`}>
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-[13.5px] truncate flex-1 min-w-0 ${open ? 'font-extrabold' : 'font-bold'} text-txt-primary`}>
            <Highlight text={latest.subject || '(no subject)'} q={q} />
          </span>
          {ordered.length > 1 && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-surface-tertiary text-txt-secondary shrink-0">{ordered.length}</span>}
          {attCount > 0 && <span className="inline-flex items-center gap-0.5 text-[10px] text-txt-tertiary shrink-0"><IconPaperclip className="w-3 h-3" /> {attCount}</span>}
          <span className="text-[11px] text-txt-tertiary shrink-0 tabular-nums">{fmtShort(latest.sentAt)}</span>
          <svg className={`w-3.5 h-3.5 text-txt-tertiary shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
        </div>
        {!open && (
          <p className="text-[12px] text-txt-secondary mt-0.5 truncate">
            <span className="text-txt-tertiary">{people.slice(0, 3).join(', ')}{people.length > 3 ? ` +${people.length - 3}` : ''}</span>
            <span className="mx-1.5 text-txt-tertiary">—</span>
            {hit ? <>…<Highlight text={hit} q={q} />…</> : (latest.snippet || '')}
          </p>
        )}
      </button>
      {open && (
        <div>
          {ordered.map((m, i) => (
            <MessageItem key={m.id} m={m} q={q} open={openMsgs.has(m.id)} onToggle={() => toggleMsg(m.id)} last={i === ordered.length - 1} />
          ))}
        </div>
      )}
    </div>
  )
}

const ReviewModal: React.FC<{ items: EmailReviewItem[]; onClose: () => void; onResolved: () => void }> = ({ items, onClose, onResolved }) => (
  // House Overlay (UI audit 21 Aug): Escape + standard backdrop; inner area scrolls.
  <Overlay onClose={onClose} width={560}>
    {/* min-h-0: without it a long list defeats the Overlay's 92vh cap instead of scrolling. */}
    <div className="p-5 space-y-3 overflow-y-auto min-h-0">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-txt-primary">Unmatched emails ({items.length})</p>
        <CloseButton onClick={onClose} />
      </div>
      <p className="text-[11px] text-txt-tertiary">Ambiguous matches wait here rather than guessing — pick the right lead, or discard. Confirming also teaches the matcher the address.</p>
      {items.map((it) => (
        <div key={it.id} className="rounded-lg border border-border p-3 space-y-1.5">
          <p className="text-[12px] font-semibold text-txt-primary">{it.subject || '(no subject)'}</p>
          <p className="text-[10px] text-txt-tertiary">{it.direction === 'in' ? `from ${it.from}` : `to ${it.from}`} · {fmtWhen(it.sentAt)} · via {it.account} · {it.reason.replace(/_/g, ' ')}</p>
          {it.snippet && <p className="text-[11px] text-txt-secondary line-clamp-2">{it.snippet}</p>}
          <div className="flex flex-wrap gap-1.5 pt-1">
            {it.candidates.map((c) => (
              <button key={c.dealId} onClick={() => resolveEmailReview(it.id, c.dealId).then(() => { crmToast(`Matched to ${c.name}`, 'success'); onResolved() }).catch(() => crmToast('Could not resolve', 'error'))}
                className="px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-border text-txt-primary hover:bg-surface-hover">
                {c.name}
              </button>
            ))}
            <button onClick={() => resolveEmailReview(it.id, null).then(() => { crmToast('Discarded', 'success'); onResolved() }).catch(() => crmToast('Could not resolve', 'error'))}
              className="px-2.5 py-1 rounded-lg text-[11px] text-txt-tertiary border border-border hover:bg-surface-hover">
              Not a lead — discard
            </button>
          </div>
        </div>
      ))}
      {items.length === 0 && <p className="text-[12px] text-txt-tertiary">Nothing waiting.</p>}
    </div>
  </Overlay>
)

type SortKey = 'newest' | 'oldest' | 'attachments'

export const DealEmails: React.FC<{ dealId: string }> = ({ dealId }) => {
  const { currentAgent } = useAuth()
  const [emails, setEmails] = useState<LeadEmail[] | null>(null)
  const [accounts, setAccounts] = useState<EmailAccount[]>([])
  const [review, setReview] = useState<EmailReviewItem[]>([])
  const [showReview, setShowReview] = useState(false)
  const [q, setQ] = useState('')
  const [sender, setSender] = useState<string>('all') // 'all' | account email | 'lead'
  const [dir, setDir] = useState<'all' | 'in' | 'out'>('all')
  const [withAtt, setWithAtt] = useState(false)
  const [sort, setSort] = useState<SortKey>('newest')
  const [page, setPage] = useState(0)

  const load = () => {
    getDealEmails(dealId).then(setEmails).catch(() => setEmails([]))
    getEmailAccounts().then(setAccounts).catch(() => {})
    getEmailReview().then(setReview).catch(() => {})
  }
  useEffect(load, [dealId])
  useEffect(() => { setPage(0) }, [q, sender, dir, withAtt, sort])

  const connect = () =>
    startGmailConnect()
      .then((r: any) => {
        if (!r.url) { crmToast('Gmail sync needs its one-off Google setup first — see docs/CRM-EMAIL-SYNC-SETUP.md (OAuth client + env vars)', 'error'); return }
        window.open(r.url, '_blank')
      })
      .catch((e: any) => crmToast(e?.message || 'Could not start Gmail connect', 'error'))
  const disconnect = (email: string) =>
    window.confirm(`Disconnect ${email}? Sync stops; already-ingested emails stay.`) &&
    disconnectGmail(email).then(() => { crmToast('Disconnected', 'success'); load() }).catch(() => crmToast('Could not disconnect', 'error'))

  // Sender options = the staff mailboxes present in this lead's history + "the lead".
  const senderOptions = useMemo(() => Array.from(new Set((emails || []).map((m) => m.account))), [emails])

  const filtered = useMemo(() => {
    let list = emails || []
    if (sender === 'lead') list = list.filter((m) => m.direction === 'in')
    else if (sender !== 'all') list = list.filter((m) => (m.direction === 'out' ? m.account === sender : m.account === sender))
    if (dir !== 'all') list = list.filter((m) => m.direction === dir)
    if (withAtt) list = list.filter((m) => m.attachments.length > 0)
    const needle = q.trim().toLowerCase()
    if (needle) {
      list = list.filter((m) =>
        `${m.subject || ''} ${m.from} ${m.to.join(' ')} ${m.cc.join(' ')}`.toLowerCase().includes(needle) ||
        (m.body || '').toLowerCase().includes(needle) ||
        (m.snippet || '').toLowerCase().includes(needle) ||
        m.attachments.some((a) => (a.filename || '').toLowerCase().includes(needle)))
    }
    const byDate = (a: LeadEmail, b: LeadEmail) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime()
    if (sort === 'newest') list = [...list].sort(byDate)
    else if (sort === 'oldest') list = [...list].sort((a, b) => byDate(b, a))
    else list = [...list].sort((a, b) => (Number(b.attachments.length > 0) - Number(a.attachments.length > 0)) || byDate(a, b))
    return list
  }, [emails, q, sender, dir, withAtt, sort])

  // ONE THREAD = ONE CARD (Lee, 14 Oct 2026): group the filtered messages by
  // Gmail thread (fallback: normalised subject), newest-activity threads first
  // (or per the chosen sort).
  const threads = useMemo(() => {
    const byThread = new Map<string, LeadEmail[]>()
    for (const m of filtered) {
      const key = m.threadId || `s:${(m.subject || '').toLowerCase().replace(/^(re|fwd?):\s*/g, '').trim()}`
      if (!byThread.has(key)) byThread.set(key, [])
      byThread.get(key)!.push(m)
    }
    const latestAt = (list: LeadEmail[]) => Math.max(...list.map((m) => new Date(m.sentAt).getTime()))
    const out = [...byThread.values()]
    if (sort === 'oldest') out.sort((a, b) => latestAt(a) - latestAt(b))
    else if (sort === 'attachments') out.sort((a, b) => (Number(b.some((m) => m.attachments.length > 0)) - Number(a.some((m) => m.attachments.length > 0))) || latestAt(b) - latestAt(a))
    else out.sort((a, b) => latestAt(b) - latestAt(a))
    return out
  }, [filtered, sort])

  const pageCount = Math.max(1, Math.ceil(threads.length / PAGE_SIZE))
  const clamped = Math.min(page, pageCount - 1)
  const pageRows = threads.slice(clamped * PAGE_SIZE, clamped * PAGE_SIZE + PAGE_SIZE)
  const needle = q.trim()

  const chip = (active: boolean) =>
    `inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${active ? 'bg-txt-primary text-surface border-txt-primary' : 'border-border text-txt-secondary hover:bg-surface-hover'}`

  return (
    <div className="max-w-[860px]">
      {/* ── STICKY header: everything above the list stays put while it scrolls. ── */}
      <div className="sticky top-0 z-10 bg-surface pb-3 space-y-2.5 border-b border-border mb-3">
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary flex-1">Email history {emails ? `(${threads.length} threads · ${filtered.length}${filtered.length !== emails.length ? ` of ${emails.length}` : ''} emails)` : ''}</p>
          {review.length > 0 && (
            <button onClick={() => setShowReview(true)} className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 hover:bg-amber-200">
              {review.length} unmatched
            </button>
          )}
          {accounts.filter((a) => a.status !== 'revoked').map((a) => (
            <span key={a.googleEmail}
              className={`px-2 py-0.5 rounded-full text-[10px] border cursor-pointer ${a.status === 'active' ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-red-300 bg-red-50 text-red-700'}`}
              title={`Synced for ALL leads · ${a.status}${a.lastSyncAt ? ` · last sync ${fmtWhen(a.lastSyncAt)}` : ''}${a.lastError ? ` · ${a.lastError}` : ''} — click to disconnect`}
              onClick={() => disconnect(a.googleEmail)}
              role="button"
            >
              ✓ {a.googleEmail}{a.status === 'needs_reauth' ? ' — re-link needed' : ''}
            </span>
          ))}
          {!accounts.some((a) => a.status === 'active' && (a.agentEmail === currentAgent?.email || a.googleEmail === currentAgent?.email)) && (
            <button onClick={connect} title="One-off — links your mailbox for every lead, not just this one"
              className="px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-border text-txt-primary hover:bg-surface-hover">
              + Connect your Gmail (one-off, all leads)
            </button>
          )}
        </div>

        {/* Search — subject, people, body content, attachment names (house SearchInput). */}
        <SearchInput value={q} onChange={setQ} placeholder="Search emails — subject, people or content…" />

        {/* Filters + sort. */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <button onClick={() => setSender('all')} className={chip(sender === 'all')}>All senders</button>
          {senderOptions.map((s) => (
            <button key={s} onClick={() => setSender(s)} className={chip(sender === s)} title={`Emails in ${s}'s mailbox`}>{s.split('@')[0]}@</button>
          ))}
          <button onClick={() => setSender('lead')} className={chip(sender === 'lead')} title="Only emails FROM the lead">From the lead</button>
          <span className="w-px h-4 bg-border mx-1" />
          <button onClick={() => setDir(dir === 'in' ? 'all' : 'in')} className={chip(dir === 'in')}><IconMailIn className="w-3 h-3" /> In</button>
          <button onClick={() => setDir(dir === 'out' ? 'all' : 'out')} className={chip(dir === 'out')}><IconMailOut className="w-3 h-3" /> Out</button>
          <button onClick={() => setWithAtt((v) => !v)} className={chip(withAtt)}><IconPaperclip className="w-3 h-3" /> Has attachments</button>
          <span className="flex-1" />
          <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}
            className="rounded-lg border border-border bg-surface-secondary/40 px-2 py-1 text-[11px] text-txt-secondary focus:outline-none">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="attachments">Attachments first</option>
          </select>
        </div>
      </div>

      {/* ── The list — scrolls under the sticky header. ── */}
      {emails === null ? (
        <p className="text-sm text-txt-tertiary">Loading emails…</p>
      ) : pageRows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-8 text-center">
          <p className="text-sm text-txt-secondary">{filtered.length === 0 && (q || sender !== 'all' || dir !== 'all' || withAtt) ? 'Nothing matches these filters.' : 'No email history for this lead yet.'}</p>
          {!q && filtered.length === 0 && sender === 'all' && (
            <p className="text-[11px] text-txt-tertiary mt-1">
              {accounts.some((a) => a.status === 'active')
                ? 'Connected mailboxes are synced every few minutes — matching mail appears here automatically.'
                : 'Connect a staff Gmail above — correspondence with this lead (including the last 12 months) will pull in automatically.'}
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {pageRows.map((t) => <ThreadCard key={t[0].threadId || t[0].id} msgs={t} q={needle} />)}
          </div>
          {pageCount > 1 && (
            <div className="flex items-center justify-between mt-3 pb-2">
              <button disabled={clamped === 0} onClick={() => setPage(clamped - 1)}
                className="px-3 py-1.5 rounded-lg text-[11px] font-semibold border border-border text-txt-secondary disabled:opacity-40 hover:bg-surface-hover">← Prev</button>
              <span className="text-[11px] text-txt-tertiary">
                {clamped * PAGE_SIZE + 1}–{Math.min(threads.length, (clamped + 1) * PAGE_SIZE)} of {threads.length} threads · page {clamped + 1}/{pageCount}
              </span>
              <button disabled={clamped >= pageCount - 1} onClick={() => setPage(clamped + 1)}
                className="px-3 py-1.5 rounded-lg text-[11px] font-semibold border border-border text-txt-secondary disabled:opacity-40 hover:bg-surface-hover">Next →</button>
            </div>
          )}
        </>
      )}
      {showReview && <ReviewModal items={review} onClose={() => setShowReview(false)} onResolved={load} />}
    </div>
  )
}
